// ============================================================================
// App Configuration Types
// ============================================================================

export interface AppConfig {
  // Authentication
  username: string;
  password: string;
  authId: string; // Base64(username:password) for Authorization header

  // Connection
  agentName: string;
  websocketUrl: string; // e.g., "https://xora01.x-or.cloud/"
  websocketPath: string; // e.g., "/ws/socket.io"

  // Voice API
  voiceAPIKey: string; // ElevenLabs API Key

  // Text-to-Speech Settings
  ttsProvider: 'elevenlabs'; // Only ElevenLabs for now
  ttsModel: 'eleven_flash_v2_5' | 'eleven_turbo_v2_5' | 'eleven_multilingual_v2';
  voiceGender: 'male' | 'female' | 'female2' | 'female3';
  voiceId: string; // Auto-mapped from voiceGender

  // Speech-to-Text Settings
  sttProvider: 'elevenlabs'; // Only ElevenLabs for now
  sttModel: 'scribe_v1'; // Only scribe_v1 for now

  // PTT Settings
  pttMode: 'hid' | 'manual'; // Current PTT mode

  // Optional settings
  autoReconnect: boolean; // Default: true
  cameraEnabled: boolean; // Default: true
  voiceModeEnabled: boolean; // Default: true - Enable PTT, STT, TTS
  theme: 'light' | 'dark'; // Default: 'light'

  // Metadata
  lastConnected?: number; // Timestamp
  version: string; // Config version
}

// ============================================================================
// WebSocket Event Types
// ============================================================================

// Event naming convention: <object>.<verb>

// ---------- Client → Server Events ----------

export interface UserConnectEvent {
  agentName: string;
  timestamp: number;
  sessionId: string; // uuid-v4
}

export interface ImageAttachment {
  type: 'image/base64';
  content: string; // data:image/jpeg;base64,...
  metadata: {
    width: number;
    height: number;
    captureTime: number;
  };
}

export interface UserMessageEvent {
  content: string; // Transcript from STT
  attachments?: ImageAttachment[];
  metadata: {
    pttMode: 'hid' | 'manual';
    confidence: number; // From STT API
    audioLength: number; // ms
  };
  timestamp: number;
}

export interface UserDisconnectEvent {
  agentName: string;
  timestamp: number;
  reason: 'user_action' | 'timeout' | 'error';
}

export interface ConsoleClearEvent {
  timestamp: number;
}

// ---------- Server → Client Events ----------

export type AgentStatus = 'idle' | 'busy' | 'offline';

export interface AgentUpdateEvent {
  name: string;
  status: AgentStatus;
  timestamp: number;
  metadata?: {
    version?: string;
    capabilities?: string[];
  };
}

export interface AgentActionEvent {
  type: 'init' | 'message' | 'tool_use' | 'tool_result' | 'result';
  content: string; // Human-readable description
  metadata?: Record<string, unknown>;
  payload: AgentActionPayload;
  usage?: {
    inputTokens: number;
    outputTokens: number;
    duration: number;
    totalTurns: number;
  };
  timestamp: number;
}

export type AgentActionPayload =
  | InitPayload
  | MessagePayload
  | ToolUsePayload
  | ToolResultPayload
  | ResultPayload;

export interface InitPayload {
  type: 'system';
  subtype: 'init';
  session_id: string;
  tools: string[];
  mcp_servers: Array<{ name: string; status: string }>;
  model: string;
}

export interface MessagePayload {
  type: 'text';
  text: string;
}

export interface ToolUsePayload {
  type: 'tool_use';
  id: string;
  name: string;
  input: Record<string, unknown>;
}

export interface ToolResultPayload {
  tool_use_id: string;
  type: 'tool_result';
  content: string;
}

export interface ResultPayload {
  type: 'result';
  subtype: 'success' | 'error';
  is_error: boolean;
  message?: string;
}

export interface ConsoleUpdateEvent {
  id: string;
  timestamp: string;
  agent: string;
  message?: string; // For TTS
  sections: ConsoleSection[];
}

export type ConsoleSection =
  | MarkdownSection
  | MermaidSection
  | ChartSection
  | HTMLSection
  | IframeSection;

export interface MarkdownSection {
  type: 'markdown';
  data: {
    content: string;
  };
}

export interface MermaidSection {
  type: 'mermaid';
  data: {
    content: string;
  };
}

export interface ChartSection {
  type: 'chart';
  data: {
    title: string;
    chartType: 'bar' | 'line' | 'pie' | 'area';
    labels: string[];
    values: number[];
  };
}

export interface HTMLSection {
  type: 'html';
  data: {
    content: string;
  };
}

export interface IframeSection {
  type: 'iframe';
  data: {
    url: string;
    height?: string;
  };
}

export interface AgentErrorEvent {
  code: 'AGENT_TIMEOUT' | 'PROCESSING_ERROR' | 'TOOL_ERROR';
  message: string;
  details?: Record<string, unknown>;
  timestamp: number;
}

// ============================================================================
// PTT & Audio Types
// ============================================================================

export enum PTTMode {
  HID = 'hid',
  MANUAL = 'manual',
}

export type RecordingStatus = 'idle' | 'listening';

export interface AudioRecording {
  blob: Blob;
  duration: number;
  timestamp: number;
}

export interface STTResult {
  transcript: string;
  confidence: number;
}

// ============================================================================
// ElevenLabs API Types
// ============================================================================

export interface ElevenLabsConfig {
  apiKey: string;
  baseUrl: string; // 'https://api.elevenlabs.io/v1'
  tts: {
    voiceId: string;
    modelId: 'eleven_multilingual_v2';
    voiceSettings: {
      stability: number;
      similarity_boost: number;
      style: number;
      use_speaker_boost: boolean;
    };
  };
  stt: {
    model: 'eleven_multilingual_v2';
    language: 'vi';
  };
}

// ============================================================================
// Camera Types
// ============================================================================

export interface CameraCapture {
  dataUrl: string; // data:image/jpeg;base64,...
  width: number;
  height: number;
  timestamp: number;
}

// ============================================================================
// Console State Types
// ============================================================================

export interface LogEntry {
  id: string;
  timestamp: number;
  type: 'user' | 'agent' | 'system' | 'error';
  content: string;
  metadata?: Record<string, unknown>;
}

export interface ConsoleState {
  sections: ConsoleSection[];
  logs: LogEntry[];
  agentStatus: AgentStatus;
  isConnected: boolean;
}
